import { useState, useEffect, useCallback } from 'react';
import Card from '../components/ui/Card';
import Pagination from '../components/ui/Pagination';
import Modal from '../components/ui/Modal';
import Toast from '../components/ui/Toast';
import Button from '../components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { fetchWithAdminAuth } from '../utils/adminAuth';

interface Payment {
  id: string;
  userId: string;
  userEmail: string;
  userName?: string;
  amount: number;
  paymentType: string;
  status: string;
  createdAt: string;
  description?: string;
  nif?: string | null;
  mbReference?: string | null;
  mbEntity?: string | null;
  duration?: number;
  source?: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pendente', color: 'yellow' },
  { value: 'Paid', label: 'Pago', color: 'green' },
  { value: 'Failed', label: 'Falhado', color: 'red' },
  { value: 'Cancelled', label: 'Cancelado', color: 'red' },
];

export default function Pagamentos() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  
  // Filter states
  const [searchEmail, setSearchEmail] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Status change modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchEmail), 400);
    return () => clearTimeout(timer);
  }, [searchEmail]);

  // Reload on filter change (reset to page 1)
  useEffect(() => {
    fetchPayments(1);
  }, [debouncedSearch, selectedPaymentType, startDate, endDate]);

  const fetchPayments = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (selectedPaymentType !== 'all') params.set('paymentType', selectedPaymentType);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const response = await fetchWithAdminAuth(`/api/payments/getAllPayments?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch payments: ${response.statusText}`);
      }
      
      const data = await response.json();
      setPayments(data?.payments ?? []);
      setPagination(data?.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedPaymentType, startDate, endDate]);

  const handlePageChange = (page: number) => {
    fetchPayments(page);
  };

  const openStatusModal = (payment: Payment) => {
    setSelectedPayment(payment);
    setNewStatus(payment.status);
    setStatusModalOpen(true);
  };

  const handleStatusChange = async () => {
    if (!selectedPayment || !newStatus) return;
    if (newStatus === selectedPayment.status) {
      setStatusModalOpen(false);
      return;
    }

    setStatusLoading(true);
    try {
      const res = await fetchWithAdminAuth(`/api/payments/${selectedPayment.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await res.json();

      if (!res.ok) {
        setToast({ message: data.error || 'Erro ao atualizar estado', type: 'error' });
        return;
      }

      setToast({ message: data.message || 'Estado atualizado com sucesso', type: 'success' });
      setStatusModalOpen(false);

      // Refresh payments list
      fetchPayments(pagination.page);
    } catch (err) {
      console.error('Error updating payment status:', err);
      setToast({ message: 'Erro ao atualizar estado do pagamento', type: 'error' });
    } finally {
      setStatusLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const normalized = status.toLowerCase();
    
    if (['paid', 'completed', 'success'].includes(normalized)) {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
          Pago
        </span>
      );
    }
    
    if (['pending', 'processing', 'awaiting'].includes(normalized)) {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          Pendente
        </span>
      );
    }

    if (['cancelled'].includes(normalized)) {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-slate-500/20 text-slate-300 border border-slate-500/30">
          Cancelado
        </span>
      );
    }
    
    return (
      <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
        Falhado
      </span>
    );
  };

  if (error && payments.length === 0) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-5">
        <Card
          title="Pagamentos"
          subtitle="Lista de carregamentos de saldo (IA e humanos), métodos de pagamento e estado."
        >
          <div className="text-center py-12 text-red-400">
            <p>Erro ao carregar pagamentos: {error}</p>
            <button
              onClick={() => fetchPayments(1)}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Tentar novamente
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Card
        title="Pagamentos"
        subtitle="Lista de carregamentos de saldo (IA e humanos), métodos de pagamento e estado."
      >
        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search by email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Pesquisar por email/nome
            </label>
            <input
              type="text"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="email@exemplo.com"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
            />
          </div>

          {/* Payment method filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Método de pagamento
            </label>
            <select
              value={selectedPaymentType}
              onChange={(e) => setSelectedPaymentType(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
            >
              <option value="all">Todos</option>
              <option value="MBway">MBway</option>
              <option value="RefMB">Referência MB</option>
              <option value="Stripe">Stripe</option>
            </select>
          </div>

          {/* Start date */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Data início
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
            />
          </div>

          {/* End date */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Data fim
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-400">A carregar pagamentos...</span>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead>Valor (c/ IVA)</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Nenhum pagamento encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {new Date(payment.createdAt).toLocaleString('pt-PT', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>{payment.userName || 'N/A'}</TableCell>
                        <TableCell className="text-[#94a3b8]">{payment.userEmail}</TableCell>
                        <TableCell>{payment.paymentType}</TableCell>
                        <TableCell>€ {payment.amount.toFixed(2)}</TableCell>
                        <TableCell>{getStatusBadge(payment.status)}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => openStatusModal(payment)}
                            className="text-[0.78rem] text-[#60a5fa] hover:underline"
                          >
                            Alterar estado
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              pageSize={pagination.limit}
              itemsOnPage={payments.length}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Card>

      {/* Status Change Modal */}
      <Modal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title="Alterar estado do pagamento"
      >
        {selectedPayment && (
          <div className="space-y-4">
            {/* Payment info summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[0.78rem] pb-3 border-b border-border-soft">
              <div>
                <span className="text-text-soft block">Cliente</span>
                <span className="text-text font-medium">{selectedPayment.userName || 'N/A'}</span>
              </div>
              <div>
                <span className="text-text-soft block">Email</span>
                <span className="text-text font-medium text-xs">{selectedPayment.userEmail}</span>
              </div>
              <div>
                <span className="text-text-soft block">Valor</span>
                <span className="text-text font-medium">€ {selectedPayment.amount.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-text-soft block">Método</span>
                <span className="text-text font-medium">{selectedPayment.paymentType}</span>
              </div>
              <div>
                <span className="text-text-soft block">Estado atual</span>
                {getStatusBadge(selectedPayment.status)}
              </div>
              <div>
                <span className="text-text-soft block">Data</span>
                <span className="text-text font-medium text-xs">
                  {new Date(selectedPayment.createdAt).toLocaleString('pt-PT')}
                </span>
              </div>
            </div>

            {/* Status selector */}
            <div>
              <label className="block text-[0.78rem] text-text-soft mb-2">Novo estado</label>
              <div className="grid grid-cols-2 gap-2">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setNewStatus(opt.value)}
                    disabled={selectedPayment.status === 'Paid' && opt.value !== 'Paid'}
                    className={`px-3 py-2.5 rounded-xl border text-[0.78rem] font-medium transition-all ${
                      newStatus === opt.value
                        ? opt.color === 'green'
                          ? 'bg-green-500/20 border-green-500/50 text-green-400'
                          : opt.color === 'yellow'
                          ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'
                          : 'bg-red-500/20 border-red-500/50 text-red-400'
                        : 'bg-white/[0.02] border-border-soft text-text-soft hover:bg-white/5'
                    } ${selectedPayment.status === 'Paid' && opt.value !== 'Paid' ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {selectedPayment.status === 'Paid' && (
                <p className="mt-2 text-[0.7rem] text-yellow-400/80">
                  ⚠️ Este pagamento já foi processado. Não é possível reverter o estado.
                </p>
              )}
            </div>

            {/* Warning when marking as Paid */}
            {newStatus === 'Paid' && selectedPayment.status !== 'Paid' && (
              <div className="rounded-xl bg-green-500/10 border border-green-500/20 p-3 text-[0.75rem]">
                <p className="text-green-300 font-medium mb-1">⚡ Ao marcar como Pago, será executado automaticamente:</p>
                <ul className="text-green-300/80 space-y-0.5 pl-4 list-disc">
                  <li>Créditos de €{selectedPayment.amount.toFixed(2)} adicionados ao cliente</li>
                  <li>Fatura gerada via Moloni</li>
                  <li>Email de confirmação enviado ao cliente</li>
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setStatusModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleStatusChange}
                disabled={statusLoading || newStatus === selectedPayment.status}
              >
                {statusLoading ? 'A processar...' : 'Confirmar alteração'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
